import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { SemanticResourceAttributes } from '@opentelemetry/semantic-conventions';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { trace, Context } from '@opentelemetry/api';

import { dashboardDataAggregator } from './DashboardDataAggregator';

class WingmanInternalExporter {
  export(spans: any[], resultCallback: (result: any) => void) {
    spans.forEach(span => {
      if (typeof window !== 'undefined' && (window as any).onWingmanTrace) {
        (window as any).onWingmanTrace(span);
      }
    });
    resultCallback({ code: 0 });
  }
  shutdown() { return Promise.resolve(); }
}

const provider = new WebTracerProvider({
  resource: resourceFromAttributes({
    [SemanticResourceAttributes.SERVICE_NAME]: 'wingman-frontend',
  }),
  spanProcessors: [
    // Primary: Internal (Dashboard)
    new BatchSpanProcessor(new WingmanInternalExporter() as any)
  ],

});

const initTracing = () => {
  provider.register();
  registerInstrumentations({
    instrumentations: [
      new UserInteractionInstrumentation(),
      new XMLHttpRequestInstrumentation(),
      new FetchInstrumentation(),
    ],
  });
  console.info('🎨 OpenTelemetry Tracing Initialized (Non-Blocking)');
};

if (typeof window !== 'undefined') {
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(() => initTracing());
  } else {
    setTimeout(initTracing, 500);
  }
}

export const getTracer = (name: string) => trace.getTracer(name);

export const startSpan = (name: string, ctx?: Context) => {
  const tracer = getTracer('wingman-frontend');
  return tracer.startSpan(name, undefined, ctx);
};

export const withSpan = async <T>(
  name: string,
  fn: (span: any) => Promise<T>
): Promise<T> => {
  const tracer = getTracer('wingman-frontend');
  return tracer.startActiveSpan(name, async (span) => {
    try {
      const result = await fn(span);
      span.setStatus({ code: 1 }); // OK
      return result;
    } catch (error: any) {
      span.setStatus({ code: 2, message: error.message }); // ERROR
      span.recordException(error);
      throw error;
    } finally {
      span.end();
    }
  });
};
